<template>
  <b-input-group :prepend="currencySymbol">
    <b-input
      ref="inputRef"
      :name="name"
      :placeholder="placeholder"
      :state="invalid"
    />
  </b-input-group>
</template>

<script>
import { Validator } from 'vee-validate';
import { useCurrencyInput } from 'vue-currency-input';

import { getCurrencySymbol } from '@/febos/global/customDocuments/utils/getCurrencySymbol';

Validator.extend('minCurrencyValue', {
  validate: (value, min) => Number(value.toString().replace(/[$,.]/g, '')) >= min,
  getMessage: (_, [min]) => `El monto ingresado debe ser mayor o igual a ${min}`,
});

/**
 * Componente de input numérico para montos en moneda.
 * Permite ingresar y mostrar valores numéricos con formato de moneda (ej: $1.234).
 *
 * Props:
 * - name: Nombre del input (requerido).
 * - value: Valor inicial (puede ser número o string formateado, se limpia automáticamente).
 * - modelValue: Valor para v-model (string o número).
 * - invalid: Marca el input como inválido.
 * - placeholder: Texto de ayuda.
 * - options: Opciones de configuración para el formato de moneda (locale, currency, etc).
 *
 * Eventos emitidos:
 * - input: Cuando cambia el valor (valor limpio).
 * - number-input: Cuando cambia el valor numérico.
 * - formatted-input: Cuando cambia el valor formateado.
 *
 * Uso:
 * <number-currency-input
 *   v-model="monto"
 *   @input="onInput"
 * />
 *
 * Notas:
 * - Si el valor inicial viene formateado (ej: "$1.234"), el componente lo limpiará automáticamente.
 * - Se recomienda migrar a CurrencyInputV2.
 */
export default {
  name: 'number-currency-input',
  emits: [
    'number-input',
    'formatted-input'
  ],
  props: {
    name: { type: String, required: true },
    value: { type: [Number], default: null },
    modelValue: { type: [String, Number], default: '' },
    invalid: { type: Boolean, default: null },
    placeholder: { type: String, default: 'Ingresa un monto' },
    /**
     * @type {import('vue').PropType<CurrencyInputOptions>}
     */
    options: {
      type: Object,
      default: () => ({
        locale: 'es-CL',
        currency: 'CLP',
        hideCurrencySymbolOnFocus: false,
        hideGroupingSeparatorOnFocus: false,
        useGrouping: true,
        currencyDisplay: 'hidden',
      })
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        const cleanValue = typeof val === 'string'
          ? Number(val.replace(/[^0-9]+/g, ''))
          : val;
        this.setValue(cleanValue || null);
        this.$emit('input', cleanValue);
      }
    },
  },
  setup(props) {
    const {
      inputRef,
      numberValue,
      formattedValue,
      ...rest
    } = useCurrencyInput(props.options);
    const currencySymbol = getCurrencySymbol(
      props.options.locale,
      props.options.currency,
    );

    return {
      inputRef,
      numberValue,
      formattedValue,
      currencySymbol,
      ...rest
    };
  }
};
</script>
<style>
.input-group:has(> .form-control.is-invalid) .input-group-prepend > .input-group-text {
  border: 1px solid red;
}
</style>
